import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
  FINANCE_UNITS_TABLE_MAX_HEIGHT,
  unitSortKey,
  useClientTableSort,
} from "./useClientTableSort";
import { formatDateUk } from "../../utils/dateDisplay";

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

export default function OwnerInvoicePreviewPanel({
  preview,
  loading,
  onRefresh,
  onPostInvoice,
  onGeneratePdf,
  generatingPdfId,
  onRegenerateAll,
  regeneratingAll,
}) {
  const unitSortAccessors = useMemo(
    () => ({
      unit_code: unitSortKey,
      owner_name: (row) => row.owner_name || "",
      invoice_number: (row) => row.invoice_number || "",
      estate_charge_amount: (row) => row.estate_charge_amount,
      block_charge_amount: (row) => row.block_charge_amount,
      total: (row) => row.total,
      generated_total: (row) => row.generated_total,
    }),
    []
  );
  const { sortedRows, sortField, sortDirection, handleSort } = useClientTableSort(
    preview?.units || [],
    unitSortAccessors,
    "unit_code"
  );
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  if (loading) {
    return <Skeleton height={320} sx={{ mb: 2 }} />;
  }

  if (!preview) {
    return null;
  }

  const failedChecks = (preview.apportionment_checks || []).filter((check) => !check.within_tolerance);
  const failedNames = failedChecks.map((check) => check.section_name).join(", ");
  const failedVerb = failedChecks.length === 1 ? "does not total" : "do not total";
  const invoices = preview.units.filter((unit) => unit.invoice_id);
  const regenerableInvoices = invoices.filter((unit) => unit.invoice_status === "draft");

  return (
    <>
      <Alert severity={preview.within_tolerance ? "success" : "warning"} sx={{ mb: 2 }}>
        {preview.new_unit_count} new draft{preview.new_unit_count === 1 ? "" : "s"} to create
        {preview.existing_unit_count
          ? ` · ${preview.existing_unit_count} unit${
              preview.existing_unit_count === 1 ? "" : "s"
            } with existing invoices`
          : ""}
        {" · "}
        Total {money(preview.invoice_total)} vs budget {money(preview.budget_total)}
      </Alert>

      {failedChecks.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {failedNames} {failedVerb} 100%. Check apportionments before generating invoices.
        </Alert>
      )}

      {preview.invoice_mismatch_count > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {preview.invoice_mismatch_count} generated invoice{preview.invoice_mismatch_count === 1 ? "" : "s"} {preview.invoice_mismatch_count === 1 ? "doesn't" : "don't"} match the total.
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
        {onRegenerateAll && regenerableInvoices.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            onClick={onRegenerateAll}
            disabled={regeneratingAll}
            aria-busy={regeneratingAll}
            startIcon={
              regeneratingAll
                ? <CircularProgress size={16} color="inherit" aria-label="Regenerating invoice PDFs" />
                : <RefreshIcon />
            }
          >
            {regeneratingAll ? "Regenerating…" : "Regenerate all"}
          </Button>
        )}
      </Box>

      <TableContainer sx={{ maxHeight: FINANCE_UNITS_TABLE_MAX_HEIGHT }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortField === "unit_code" ? sortDirection : false}>
                <TableSortLabel
                  active={sortField === "unit_code"}
                  direction={sortField === "unit_code" ? sortDirection : "asc"}
                  onClick={() => handleSort("unit_code")}
                >
                  Unit
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortField === "owner_name" ? sortDirection : false}>
                <TableSortLabel
                  active={sortField === "owner_name"}
                  direction={sortField === "owner_name" ? sortDirection : "asc"}
                  onClick={() => handleSort("owner_name")}
                >
                  Owner
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortField === "invoice_number" ? sortDirection : false}>
                <TableSortLabel
                  active={sortField === "invoice_number"}
                  direction={sortField === "invoice_number" ? sortDirection : "asc"}
                  onClick={() => handleSort("invoice_number")}
                >
                  Invoice
                </TableSortLabel>
              </TableCell>
              <TableCell>Date</TableCell>
              <TableCell
                align="right"
                sortDirection={sortField === "estate_charge_amount" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortField === "estate_charge_amount"}
                  direction={sortField === "estate_charge_amount" ? sortDirection : "asc"}
                  onClick={() => handleSort("estate_charge_amount")}
                >
                  Estate Charge
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortField === "block_charge_amount" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortField === "block_charge_amount"}
                  direction={sortField === "block_charge_amount" ? sortDirection : "asc"}
                  onClick={() => handleSort("block_charge_amount")}
                >
                  Block Charges
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={sortField === "total" ? sortDirection : false}>
                <TableSortLabel
                  active={sortField === "total"}
                  direction={sortField === "total" ? sortDirection : "asc"}
                  onClick={() => handleSort("total")}
                >
                  Current total
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortField === "generated_total" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortField === "generated_total"}
                  direction={sortField === "generated_total" ? sortDirection : "asc"}
                  onClick={() => handleSort("generated_total")}
                >
                  Generated total
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((unit) => (
              <TableRow key={unit.unit_id}>
                <TableCell>{unit.unit_name}</TableCell>
                <TableCell>{unit.owner_name || "—"}</TableCell>
                <TableCell>{unit.invoice_number || "—"}</TableCell>
                <TableCell>{unit.invoice_date ? formatDateUk(unit.invoice_date) : "—"}</TableCell>
                <TableCell align="right">{money(unit.estate_charge_amount)}</TableCell>
                <TableCell align="right">{money(unit.block_charge_amount)}</TableCell>
                <TableCell align="right">{money(unit.total)}</TableCell>
                <TableCell align="right">
                  {unit.invoice_id ? (
                    <>
                      {money(unit.generated_total)}
                      {!unit.invoice_total_matches_generated && (
                        <Chip size="small" color="warning" label="Mismatch" sx={{ ml: 1 }} />
                      )}
                    </>
                  ) : (
                    <Chip size="small" label="Pending" />
                  )}
                </TableCell>
                <TableCell>
                  {unit.invoice_status === "posted" ? (
                    <Chip size="small" color="success" label="Posted" />
                  ) : unit.invoice_status === "draft" ? (
                    <Chip size="small" label="Draft" />
                  ) : (
                    <Chip size="small" color="primary" label="Preview" />
                  )}
                </TableCell>
                <TableCell>
                  {unit.invoice_id && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {unit.invoice_status === "draft" && onPostInvoice && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            onPostInvoice({
                              id: unit.invoice_id,
                              row_version: unit.row_version,
                              invoice_number: unit.invoice_number,
                            })
                          }
                        >
                          Post
                        </Button>
                      )}
                      {unit.invoice_status === "draft" && onGeneratePdf && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={generatingPdfId === unit.invoice_id}
                          onClick={() =>
                            onGeneratePdf({
                              id: unit.invoice_id,
                              invoice_number: unit.invoice_number,
                            })
                          }
                        >
                          {unit.pdf_document_id ? "Regenerate PDF" : "Generate PDF"}
                        </Button>
                      )}
                      {unit.pdf_download_url && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityOutlinedIcon />}
                            onClick={() => {
                              setViewingInvoice(unit);
                              setViewOpen(true);
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            href={unit.pdf_download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download
                          </Button>
                        </>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ "& td": { fontWeight: 600, borderTop: 2, borderColor: "divider" } }}>
              <TableCell colSpan={6}>Total</TableCell>
              <TableCell align="right">{money(preview.invoice_total)}</TableCell>
              <TableCell colSpan={3} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {onRefresh && (
        <Button size="small" variant="outlined" onClick={onRefresh} sx={{ mt: 2 }}>
          Refresh
        </Button>
      )}

      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        TransitionProps={{ onExited: () => setViewingInvoice(null) }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{viewingInvoice?.invoice_number || "Invoice"}</DialogTitle>
        <DialogContent>
          {viewingInvoice && !viewingInvoice.invoice_total_matches_generated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This PDF may be out of date. The generated total does not match the current total.
              Regenerate the PDF to refresh it.
            </Alert>
          )}
          {viewingInvoice?.pdf_download_url && (
            <Box
              component="iframe"
              title={`Invoice ${viewingInvoice.invoice_number || ""}`}
              src={viewingInvoice.pdf_download_url}
              sx={{ width: "100%", height: "75vh", border: 0 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={() => setViewOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
